import { useRef, useState } from 'react';
import { Confirmar } from './Confirmar.tsx';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { CampoDoPasso, JanelaEmBlocos } from './PassoAPasso.tsx';
import { usarFormulario } from '../lib/formulario.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { paraTela } from '../lib/datas.ts';
import { SITUACOES_DO_POST, nomeDaRede, nomeDaSituacao } from '../lib/posts.ts';
import { ErroDaApi, type AchadoNoPost, type PostDeMarketing, type PublicacaoDoPost, type RedeDoPost, type SituacaoDoPost } from '../lib/api.ts';

/**
 * ⭐ **A ESCADA DO POST** — ideia, rascunho, aprovado, publicado.
 *
 * Morava no alto da janela do post (parte 1, 15/10/2026). Com a ficha do post
 * em página própria (parte 2, PRD §50.5), ela mora na ficha, e a janela ficou
 * só com o plano. ⛔ **Muda o lugar, não o que o botão faz**: as mesmas rotas,
 * as mesmas perguntas.
 *
 * - ⛔ **Publicado pede o dia em que foi ao ar**, nunca o futuro.
 * - ⚠️ **Voltar de publicado pergunta antes**, dizendo que a data e os links
 *   saem junto.
 * - ⭐⭐ **Aprovar com achado grave pergunta antes** (parte 2). Quem pergunta
 *   primeiro é o servidor (409); a tela diz quais são e manda o "sim".
 * - ⚠️ **Aprovar com o conteúdo por gravar não anda**: o que se aprova é o
 *   que está gravado, e a conferência que vale é a do gravado.
 */

const LINK_DA_REDE = (p: PostDeMarketing, r: RedeDoPost) => (r === 'instagram' ? p.linkInstagram : p.linkLinkedin);

export function EscadaDoPost({
  post,
  hoje,
  conteudoPorGravar,
  graves,
  aoMudar,
}: {
  post: PostDeMarketing;
  /** "Hoje" do servidor: a publicação não aceita o futuro. */
  hoje: string;
  /** Há alteração no conteúdo que ainda não foi gravada. */
  conteudoPorGravar: boolean;
  /** Os achados graves do conteúdo gravado, para a pergunta de aprovar dizer quais são. */
  graves: AchadoNoPost[];
  /** Devolve o post como ficou. */
  aoMudar: (situacao: SituacaoDoPost, publicacao?: PublicacaoDoPost, aprovarComAchados?: boolean) => Promise<PostDeMarketing>;
}) {
  const [erro, setErro] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [tirarDePublicado, setTirarDePublicado] = useState<SituacaoDoPost | null>(null);
  /** A mensagem do servidor quando ele pede a confirmação de aprovar. */
  const [perguntaDeAprovar, setPerguntaDeAprovar] = useState<string | null>(null);

  function mover(situacao: SituacaoDoPost, aprovarComAchados?: boolean) {
    setErro(null);
    aoMudar(situacao, undefined, aprovarComAchados).catch((e) => {
      if (situacao === 'aprovado' && e instanceof ErroDaApi && e.status === 409) return setPerguntaDeAprovar(e.message);
      setErro(e instanceof Error ? e.message : 'Não consegui mudar a situação.');
    });
  }

  function escolher(s: SituacaoDoPost) {
    if (s === 'aprovado' && post.situacao !== 'aprovado' && conteudoPorGravar) {
      return setErro('O conteúdo tem alteração ainda não gravada. Grave antes de aprovar: o que se aprova é o que está gravado.');
    }
    if (s === post.situacao) {
      /* Publicado de novo é corrigir a data e os links. */
      if (s === 'publicado') setPublicando(true);
      return;
    }
    if (s === 'publicado') return setPublicando(true);
    if (post.situacao === 'publicado') return setTirarDePublicado(s);
    mover(s);
  }

  /* Os LUGARES, sem repetir: dois achados no mesmo campo são um lugar só. */
  const lugares = [...new Set(graves.map((a) => a.onde.toLowerCase()))];
  const listaDosGraves = lugares.length ? ` Onde: ${lugares.slice(0, 3).join('; ')}${lugares.length > 3 ? ' e mais' : ''}.` : '';

  return (
    <section className="s-folha mkt-folha-escada" aria-label="Situação do post">
      <header className="s-folha-topo">
        <h2 className="s-folha-titulo">Situação</h2>
        <p className="mkt-nota">Anda na hora. Aprovar com achado grave na conferência pergunta antes.</p>
      </header>
      <div className="s-folha-corpo mkt-situacao">
        <div className="mkt-escada" role="radiogroup" aria-label="Situação">
          {SITUACOES_DO_POST.map((s) => (
            <button
              key={s.id}
              type="button"
              role="radio"
              aria-checked={post.situacao === s.id}
              title={s.ajuda}
              className={`mkt-degrau mkt-sit-${s.id}${post.situacao === s.id ? ' on' : ''}`}
              onClick={() => escolher(s.id)}
            >
              <i aria-hidden="true" />
              {s.nome}
            </button>
          ))}
        </div>
        {post.situacao === 'publicado' && post.publicadoEm ? (
          <div className="mkt-no-ar">
            <span>
              <Icone nome="concluido" tamanho={14} /> No ar desde <b className="dado">{paraTela(post.publicadoEm)}</b>
            </span>
            {post.redes.map((r) =>
              LINK_DA_REDE(post, r) ? (
                <a key={r} className="mkt-link" href={LINK_DA_REDE(post, r)} target="_blank" rel="noopener noreferrer">
                  <Icone nome={r} tamanho={14} /> {nomeDaRede(r)} <Icone nome="seta-fora" tamanho={12} />
                </a>
              ) : (
                <span key={r} className="mkt-sem-link">
                  <Icone nome={r} tamanho={14} /> sem o link do {nomeDaRede(r)}
                </span>
              ),
            )}
            <button type="button" className="imv-bt p" onClick={() => setPublicando(true)}>
              <Icone nome="lapis" tamanho={13} /> Corrigir a publicação
            </button>
          </div>
        ) : (
          <small className="mkt-situacao-nota">
            {SITUACOES_DO_POST.find((s) => s.id === post.situacao)?.ajuda} Publicado pede o dia em que foi ao ar.
          </small>
        )}
        {erro && <p className="erro mkt-erro">{erro}</p>}
      </div>

      {publicando && (
        <PublicarPost
          post={post}
          hoje={hoje}
          aoFechar={() => setPublicando(false)}
          aoConfirmar={async (pub) => {
            await aoMudar('publicado', pub);
            setErro(null);
            setPublicando(false);
          }}
        />
      )}

      {tirarDePublicado && (
        <Confirmar
          titulo="Tirar de publicado"
          descricao={`O post passa a ${nomeDaSituacao(tirarDePublicado).toLowerCase()} e deixa de constar como publicado. A data em que foi ao ar e os links saem junto.`}
          rotuloAcao="Sim, tirar de publicado"
          aoFechar={() => setTirarDePublicado(null)}
          aoConfirmar={() => {
            const s = tirarDePublicado;
            setTirarDePublicado(null);
            mover(s);
          }}
        />
      )}

      {perguntaDeAprovar && (
        <Confirmar
          titulo="Aprovar com achado grave"
          descricao={`${perguntaDeAprovar}${listaDosGraves} A conferência avisa e não bloqueia: quem decide é você.`}
          rotuloAcao="Aprovar assim mesmo"
          tom="primario"
          aoFechar={() => setPerguntaDeAprovar(null)}
          aoConfirmar={() => {
            setPerguntaDeAprovar(null);
            mover('aprovado', true);
          }}
        />
      )}
    </section>
  );
}

/**
 * ⭐ **MARCAR COMO PUBLICADO** — o dia em que foi ao ar e o link em cada rede.
 *
 * ⚠️ **Os links podem ficar para depois** (os três posts de 15/10/2026 entraram
 * sem eles); a data, não. ⛔ **O futuro não entra**: post que ainda não saiu é
 * plano, e o plano é a data prevista.
 */
function PublicarPost({
  post,
  hoje,
  aoFechar,
  aoConfirmar,
}: {
  post: PostDeMarketing;
  hoje: string;
  aoFechar: () => void;
  aoConfirmar: (pub: PublicacaoDoPost) => Promise<void>;
}) {
  const corrigindo = post.situacao === 'publicado';
  const [p, setP] = useState<PublicacaoDoPost>({
    publicadoEm: post.publicadoEm ?? hoje,
    linkInstagram: post.linkInstagram,
    linkLinkedin: post.linkLinkedin,
  });
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const emVoo = useRef(false);
  const guarda = usarFormulario(p, aoFechar);
  usarDialogo(guarda.fechar, !guarda.pendencia);

  function enviar() {
    if (!p.publicadoEm) return setErro('Diga o dia em que o post foi ao ar.');
    if (p.publicadoEm > hoje) return setErro('Um post não é publicado no futuro. Para marcar quando ele vai sair, use a data prevista.');
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setGravando(true);
    setErro(null);
    try {
      /* Só vai o link das redes do post; a outra fica como está, vazia. */
      await aoConfirmar({
        publicadoEm: p.publicadoEm,
        linkInstagram: post.redes.includes('instagram') ? p.linkInstagram.trim() : '',
        linkLinkedin: post.redes.includes('linkedin') ? p.linkLinkedin.trim() : '',
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui gravar.');
      emVoo.current = false;
      setGravando(false);
    }
  }

  return (
    <>
      <JanelaEmBlocos
        rotulo={corrigindo ? 'Corrigir a publicação' : 'Marcar como publicado'}
        sobrelinha="Marketing · publicação"
        titulo={corrigindo ? 'Corrigir a publicação' : 'Marcar como publicado'}
        sub={
          <>
            <span className="mkt-tema-da-publicacao">{post.titulo}</span>
            O dia em que foi ao ar e o endereço em cada rede. Os links podem ficar para depois.
          </>
        }
        aoFechar={guarda.fechar}
        erro={erro}
        rodape={
          <>
            <span className="imv-esp" />
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <button type="button" className="imv-bt primario" disabled={gravando} onClick={enviar}>
              <Icone nome="check" tamanho={14} /> {gravando ? 'Gravando…' : corrigindo ? 'Gravar' : 'Marcar como publicado'}
            </button>
          </>
        }
      >
        <div className="imv-grade-campos">
          <CampoDoPasso rotulo="Foi ao ar em" obrigatorio col={3} mono>
            <input
              type="date"
              value={p.publicadoEm}
              max={hoje}
              onChange={(e) => {
                setErro(null);
                setP((x) => ({ ...x, publicadoEm: e.target.value }));
              }}
              autoFocus
            />
          </CampoDoPasso>
          {post.redes.map((r) => (
            <CampoDoPasso key={r} rotulo={`Link no ${nomeDaRede(r)}`} col={6} mono>
              <input
                value={r === 'instagram' ? p.linkInstagram : p.linkLinkedin}
                placeholder={r === 'instagram' ? 'instagram.com/p/…' : 'linkedin.com/feed/update/…'}
                maxLength={500}
                onChange={(e) => {
                  setErro(null);
                  const v = e.target.value;
                  setP((x) => (r === 'instagram' ? { ...x, linkInstagram: v } : { ...x, linkLinkedin: v }));
                }}
              />
            </CampoDoPasso>
          ))}
        </div>
      </JanelaEmBlocos>

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue="a publicação"
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </>
  );
}
